import { useEffect, useRef, useState } from "react";
import "./AppCustomPropertyNew.css";
import { CreateCustomPropertyContract, CustomProperty, CustomPropertyType, RoleType, EmployeeType } from "winslow-core";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { MySwitch } from "../../shared/MySwitch";
import { SETTINGS_DEFAULT, SETTINGS_DISPLAY, SHORT_SETTINGS, ROLE_DISPLAY, EMPLOYEE_TYPE_DISPLAY } from "./customPropertyConfig";
import { AppCustomPropertySettings } from "./AppCustomPropertySettings";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { apiCreateCustomProperty, apiGetCustomPropertiesForWorkspace } from "../../../services/EmployeeRecordService";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import { AppChanges } from "../app-changes/AppChanges";
import { Calendar, Check, Circle, FileUser, Hash, LetterText, ListTodo, Mail, Phone, Rows3, TextCursorInput, Upload, UserRoundSearch, Shield, Users, User } from "lucide-react";
import { toastFail, toastSuccess } from "../../../config";

export const SETTINGS_COMPONENTS = {
    SHORT: <TextCursorInput  size={16}/>,
    LONG: <LetterText size={16}/>,
    // EMAIL: <CirclePlus size={16} />,
    // WEBSITE: <CirclePlus size={16} />,
    CHECKBOX_SINGLE: <Check size={16} />,
    CHECKBOX_MULTIPLE: <ListTodo size={16} />,
    DROPDOWN: <Rows3 size={16}/>,
    EMAIL: <Mail size={16} />,
    EMPLOYEE_RECORD: <UserRoundSearch size={16} />,
    PTO_POLICIES: <FileUser size={16}/>,
    RADIO: <Circle size={16} />,
    DATE: <Calendar size={16} />,
    FILE: <Upload size={16} />,
    NUMBER: <Hash size={16} />,
    PHONE: <Phone size={16} />,
    RICH: <UserRoundSearch size={16} />
}

// Role icon mapping (same as in AppCustomPropertyEdit.tsx)
const ROLE_ICONS = {
    [RoleType.ADMIN]: <Shield size={16} className="role-icon" />,
    [RoleType.MANAGER]: <Users size={16} className="role-icon" />,
    [RoleType.EMPLOYEE]: <User size={16} className="role-icon" />,
};

type AppCustomPropertyNewProps = {
    
}

const initialState: CustomProperty = {
    id: "",
    type: CustomPropertyType.SHORT,
    label: "",
    display: "",
    description: "",
    system: false,
    required: false,
    hidden: false,
    visibleRoles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.EMPLOYEE],
    visibleEmployeeTypes: [
        EmployeeType.US_FULL_TIME,
        EmployeeType.US_PART_TIME,
        EmployeeType.US_CONTRACTOR,
        EmployeeType.US_VENDOR,
        EmployeeType.INT_CONTRACTOR,
        EmployeeType.INT_FULL_TIME,
    ],
    settings: "",
}

export const AppCustomPropertyNew = ({} : AppCustomPropertyNewProps) => {

    const storeUser = useSelector(selectUser);
    const navigate = useNavigate();

    const [hasChanges, setHasChanges] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const [editingLabel, setEditingLabel] = useState(false);
    const [edittedLabel, setEdittedLabel] = useState(false);
    const editingRef = useRef(null);
    
    const [newCustomProperty, setNewCustomProperty] = useState<CustomProperty>(initialState);
    const [settings, setSettings] = useState<any>(SHORT_SETTINGS);

    const [selectedRoles, setSelectedRoles] = useState<RoleType[]>([RoleType.ADMIN, RoleType.MANAGER, RoleType.EMPLOYEE]);
    const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState<EmployeeType[]>([]);

    useEffect(() => {
        document.addEventListener("mousedown", eventMouseDown);
        return () => {
            document.removeEventListener("mousedown", eventMouseDown);
        }
    }, []);

    const eventMouseDown = (event: any) => {
        const current = editingRef.current as any;
        if (current && !current.contains(event.target)) {
            setEditingLabel(false);
        }
    }
    
    const handleAttributeChange = (event: any) => {
        console.log("[AppCustomPropertyNew] value: " + event.target.value);
        console.log("[AppCustomPropertyNew] name: " + event.target.name);

        const newProperty = {...newCustomProperty} as any;
        newProperty[event.target.name] = event.target.value;
        

        if (event.target.name === "label") {
            setEdittedLabel(true);
        }

        if (!edittedLabel && event.target.name === "display") {
            const newLabel = event.target.value.toLowerCase().replace(/\s/g, "_");
            newProperty.label = newLabel;
            setNewCustomProperty(newProperty);
        }   

        setNewCustomProperty(newProperty);
        setHasChanges(true);

    }

    const handleRequiredChange = (checked: boolean) => {
        console.log("[AppCustomPropertyNew] required: " + checked);

        const newProperty = {...newCustomProperty} as any;
        newProperty.required = checked;

        if (newProperty.required) {
            newProperty.hidden = false;
        }

        setNewCustomProperty(newProperty);
        setHasChanges(true);
    }

    const handleHiddenChange = (checked: boolean) => {
        console.log("[AppCustomPropertyNew] hidden: " + checked);

        const newProperty = {...newCustomProperty} as any;
        newProperty.hidden = checked;

        if (newProperty.hidden) {
            newProperty.required = false;
        }

        setNewCustomProperty(newProperty);
        setHasChanges(true);
    }

    const handleSave = async() => {
        console.log("[AppCustomPropertyNew] newCustomProperty: ", newCustomProperty);  

        if (!newCustomProperty.label) {
            toast("Label is required.", toastFail);
            return;
        }

        if (newCustomProperty.label.toLowerCase().substring(0, "system-".length) === "system-") {
            toast("Label cannot start with 'system-'.", toastFail);
            return;
        }
        
        const customProperties = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

        if (customProperties) {
            // check there is no duplicate label
            const duplicateLabel = customProperties?.find((cp) => cp.label.toLowerCase() === newCustomProperty.label.toLowerCase());
            if (duplicateLabel) {
                toast("Label must be unique.", toastFail);
                return;
            }
        }

        const updatedCustomProperty: CustomProperty = {
            ...newCustomProperty,
            visibleRoles: selectedRoles,
            visibleEmployeeTypes: selectedEmployeeTypes,
        }//
        
        const payload: CreateCustomPropertyContract["Body"] = {
            ...updatedCustomProperty,
            settings: JSON.stringify(settings || {}),
            workspaceId: storeUser?.currentWorkspaceId || "",
        }

        const cp = await apiCreateCustomProperty(payload);

        if (!cp) {
            toast("Error saving custom propery");
            return;
        }

        toast("Custom Property Saved", toastSuccess);
        navigate("/app/custom-properties/refresh");
    }

    const typeOptions: GenericSelectOption[] = Object.keys(CustomPropertyType)
        .sort((a: string, b:string) => {

            return SETTINGS_DISPLAY[a as CustomPropertyType].toLowerCase().localeCompare(SETTINGS_DISPLAY[b as CustomPropertyType].toLowerCase())
        })
        .map((key) => {
        return {
            displayName: SETTINGS_DISPLAY[key as CustomPropertyType],
            value: key,
            component: SETTINGS_COMPONENTS[key as CustomPropertyType]
        }
    });

    console.log("[AppCustomPropertyNew] typeOptions: ", typeOptions);

    const selectedTypeOption = typeOptions.find((option) => option.value === newCustomProperty.type);
    const roleOptions = Object.keys(RoleType) as string[];
    const employeeTypeOptions = Object.keys(EmployeeType) as string[];

    console.log("[AppCustomPropertyNew] newCustomProperty: ", newCustomProperty);

    return (
        <div className="app-custom-property-new">
            <div className="app-custom-property-new-header">
                <h1>Add New Custom Property</h1>
            </div>

            <div className="app-custom-property-new-contents">
                <div className="app-custom-property-new-left">
                    
                    <div className="app-custom-property-label">
                        Display Name
                    </div>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            value={newCustomProperty.display}
                            name="display"
                            onChange={handleAttributeChange}
                            autoFocus={true}
                        />
                    </div>

                    <div className="app-custom-property-sub-label">
                        <div className="app-custom-property-sub-label-title">
                            Internal name:
                        </div>
                        <div 
                            className="app-custom-property-sub-label-value"
                            ref={editingRef}
                        >
                            {editingLabel &&
                                <input 
                                    type="text"
                                    value={newCustomProperty.label}
                                    name="label"
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            
                                            if (newCustomProperty.label?.toLowerCase().substring(0, "system-".length) === "system-") {
                                                toast("Label cannot start with 'system-'.", toastFail);
                                                event.preventDefault();
                                                event.stopPropagation();
                                                return;
                                            }
                                            
                                            setEditingLabel(false);
                      
                                        }
                                    }}
                                    onChange={handleAttributeChange}
                                />
                            }   
                            {!editingLabel &&
                                <div 
                                    onClick={() => {
                                        setEditingLabel(true);
                                    }}
                                >
                                    {newCustomProperty.label}
                                </div>
                            }
                        </div>
                    </div>

                    <div className="app-custom-property-label">
                        Type
                    </div>
                    <div className="app-custom-property-value">
                        <GenericSelect 
                            selected={selectedTypeOption}
                            options={typeOptions}
                            onChange={(value: CustomPropertyType) => {
                                console.log("[AppCustomPropertyNew] type: " + value);
                                if (value !== newCustomProperty.type) {
                                    setSettings(SETTINGS_DEFAULT[value]);
                                }
                                setHasChanges(true);
                                setNewCustomProperty({...newCustomProperty, type: value});
                                
                            }}
                            maxHeight="200px"
                        />
                    </div>

                    <div className="app-custom-property-label">
                        Tab Group
                    </div>
                    <div className="app-custom-property-value">
                        <input
                            type="text"
                            value={newCustomProperty.group}
                            name="group"
                            onChange={handleAttributeChange}
                        />
                    </div>

                    <div className="app-custom-property-label">
                        Description
                    </div>
                    <div className="app-custom-property-value">
                        <textarea
                            name="description"
                            onChange={handleAttributeChange}
                        />
                    </div>

                    <div className="app-custom-property-label">
                        Roles
                    </div>
                    <div className="app-custom-property-value">
                        <div className="app-custom-property-edit-roles-horizontal">

                            {roleOptions.map((role, index) => {
                                return (
                                    <div 
                                        key={role}
                                        className="app-custom-property-role-choice">
                                        <input 
                                            type="checkbox"
                                            name="visibleRoles"
                                            checked={selectedRoles.includes(role as RoleType) || false}
                                            onChange={(event) => {
                                                setHasChanges(true);
                                                setSelectedRoles((prev) => {
                                                    if (prev.includes(role as RoleType)) {
                                                        return prev.filter((r) => r !== role);
                                                    } else {
                                                        return [...prev, role as RoleType];
                                                    }
                                                }
                                                );
                                            }}
                                        />
                                        <div className="app-custom-property-role-icon">
                                            {ROLE_ICONS[role as RoleType]}
                                        </div>
                                        <span className="app-custom-property-role-label">
                                            {ROLE_DISPLAY[role as RoleType]}
                                        </span>
                                    </div>
                                )
                            }
                            )}
                        </div>
                    </div>

                    <div className="app-custom-property-label">
                        Employee Types
                    </div>
                    <div className="app-custom-property-value">
                        <div className="app-custom-property-edit-employee-types-two-columns">

                            {employeeTypeOptions.map((employeeType, index) => {
                                return (
                                    <div 
                                        key={employeeType}
                                        className="app-custom-property-multiple-choice">
                                        <input 
                                            type="checkbox"
                                            name="visibleEmployeeTypes"
                                            checked={selectedEmployeeTypes.includes(employeeType as EmployeeType) || false}
                                            onChange={(event) => {
                                                setHasChanges(true);
                                                setSelectedEmployeeTypes((prev) => {
                                                    if (prev.includes(employeeType as EmployeeType)) {
                                                        return prev.filter((et) => et !== employeeType);
                                                    } else {
                                                        return [...prev, employeeType as EmployeeType];
                                                    }
                                                }
                                                );
                                            }}
                                        />
                                        <div className="app-custom-property-multiple-choice-label">
                                            {EMPLOYEE_TYPE_DISPLAY[employeeType as EmployeeType]}
                                        </div>
                                        
                                    </div>
                                )
                            }
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="app-custom-property-new-right">
                    <div className="app-custom-property-group">
                        <div className="app-custom-property-label">
                            Required
                        </div>
                        <div className="app-custom-property-value">
                            <MySwitch
                                id="required"
                                checked={newCustomProperty.required || false}
                                onChange={handleRequiredChange}
                            />
                        </div>
                    </div>

                    <div className="app-custom-property-group">
                        <div className="app-custom-property-label">
                            Hidden
                        </div>
                        <div className="app-custom-property-value">
                            <MySwitch
                                id="hidden"
                                checked={newCustomProperty.hidden || false}
                                onChange={handleHiddenChange}
                            />
                        </div>
                    </div>

                
                    <AppCustomPropertySettings 
                        type={newCustomProperty.type || CustomPropertyType.SHORT}
                        settings={settings}
                        onChange={(newSettings, disabled) => {
                            console.log("[AppCustomPropertyNew] new settings: ", newSettings);
                            setSettings({...newSettings});
                            setHasChanges(true);
                            setDisabled(disabled);
                        }}
                    />
                   
                </div>
                
                
            </div>

            {hasChanges &&
                <AppChanges
                    saveDisabled={newCustomProperty.display === "" || disabled}
                    onCancel={() => {
                        navigate("/app/custom-properties/");
                    }}
                    onSubmit={handleSave}
                />
            }

            {/* <div className="app-custom-property-actions">
                <button 
                    className="button-cancel"
                    onClick={() => {
                        navigate("/app/custom-properties/");
                    }}
                > 
                    Cancel
                </button>

                <button 
                    className="button-save"
                    onClick={handleSave}
                >
                    Save
                </button> */}
            {/* </div> */}
        </div>
    )
}